/**
 * ADM-041: the customer's subscription and charges, with pause, cancel and refund on their behalf.
 * Each action needs a reason, which is logged with the staff member's name.
 */
import * as stylex from "@stylexjs/stylex";
import type { AdminBilling, AdminChargeRow } from "@lattice/core";
import { formatUsd, PAUSE_MONTHS, ROUTES } from "@lattice/core";
import { useState } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { api } from "./api.ts";
import { Truncate } from "./bits.tsx";
import { Facts, History, Panel, Section, Table } from "./layout.tsx";
import { useLoad } from "./load.ts";
import { ReasonForm } from "./ReasonForm.tsx";
import { Button, ErrorText, SelectField, Status } from "./ui.tsx";
import { day, ORDER_STATUS, refusal, SUBSCRIPTION_STATUS } from "./words.ts";
type Action = {
  readonly kind: "pause";
} | {
  readonly kind: "cancel";
} | {
  readonly kind: "refund";
  readonly charge: AdminChargeRow;
};
function sendBilling(userId: string, action: Action, reason: string, months: string) {
  switch (action.kind) {
    case "pause":
      return api<AdminBilling>(ROUTES.adminPause, {
        params: {
          userId
        },
        body: {
          reason,
          months
        }
      });
    case "cancel":
      return api<AdminBilling>(ROUTES.adminCancelSubscription, {
        params: {
          userId
        },
        body: {
          reason
        }
      });
    case "refund":
      return api<AdminBilling>(ROUTES.adminRefund, {
        params: {
          orderId: action.charge.id
        },
        body: {
          reason
        }
      });
  }
}
function statusTone(status: string): "ok" | "danger" | "warn" | "neutral" {
  switch (status) {
    case "active":
    case "paid":
    case "released":
      return "ok";
    case "cancelled":
    case "refunded":
    case "past_due":
      return "danger";
    case "paused":
    case "pending_payment":
      return "warn";
    default:
      return "neutral";
  }
}
function ActionForm({
  userId,
  action,
  onDone,
  onCancel
}: {
  readonly userId: string;
  readonly action: Action;
  readonly onDone: (next: AdminBilling) => void;
  readonly onCancel: () => void;
}) {
  const [months, setMonths] = useState("1");
  async function submit(reason: string): Promise<string | null> {
    const result = await sendBilling(userId, action, reason, months);
    if (!result.ok) return refusal(result.error);
    onDone(result.data);
    return null;
  }
  const copy = action.kind === "pause" ? {
    title: "Pause the subscription",
    body: "Collection stops for the months you pick. The customer is not emailed from here.",
    action: "Pause subscription",
    tone: "primary" as const
  } : action.kind === "cancel" ? {
    title: "Cancel the subscription",
    body: "The subscription ends now. Existing pharmacy orders are not cancelled; do that from the order if the pharmacy should stop.",
    action: "Cancel subscription",
    tone: "danger" as const
  } : {
    title: `Refund ${formatUsd(action.charge.amountCents)}`,
    body: "The charge is marked refunded. The subscription stays as it is unless you pause or cancel it separately.",
    action: "Issue refund",
    tone: "danger" as const
  };
  return <Panel padded>
      <div className="x78zum5 xdt5ytf x626il7">
        <h3 className="x1ghz6dp x1rf3zrh x117lpx5 x1e5flnj">{copy.title}</h3>
        <p className="x1ghz6dp x1jty25b">{copy.body}</p>
        <ReasonForm id={`${action.kind}-reason`} hint="Logged with your name. Include the ticket number." action={copy.action} tone={copy.tone} onSubmit={submit} onCancel={onCancel}>
          {action.kind === "pause" ? <SelectField id="pause-months" label="Pause for" value={months} options={PAUSE_MONTHS.map(n => ({
          value: String(n),
          label: n === 1 ? "1 month" : `${n} months`
        }))} onChange={setMonths} /> : null}
        </ReasonForm>
      </div>
    </Panel>;
}
function SubscriptionActions({
  userId,
  billing,
  action,
  onChoose,
  onClear,
  onDone
}: {
  readonly userId: string;
  readonly billing: AdminBilling;
  readonly action: Action | null;
  readonly onChoose: (action: Action) => void;
  readonly onClear: () => void;
  readonly onDone: (next: AdminBilling) => void;
}) {
  const sub = billing.subscription;
  if (sub === null) return <p className="x1ghz6dp x1jty25b">No subscription on this account.</p>;
  const subAction = action !== null && action.kind !== "refund" ? action : null;
  return <>
      <Facts items={[["Status", <Status tone={statusTone(sub.status)}>{SUBSCRIPTION_STATUS[sub.status]}</Status>], ["Next ship", sub.nextShipOn === null ? "Not set" : day(sub.nextShipOn)], ["Paused until", sub.pausedUntil === null ? "Not paused" : day(sub.pausedUntil)], ["Cancelled", sub.cancelledAt === null ? "No" : day(sub.cancelledAt)]]} />
      {subAction !== null ? <ActionForm key={subAction.kind} userId={userId} action={subAction} onCancel={onClear} onDone={onDone} /> : sub.status === "cancelled" ? null : <div className="x78zum5 x1a02dak x1173whf">
          {sub.status === "paused" ? null : <Button tone="quiet" onClick={() => onChoose({
        kind: "pause"
      })}>
              Pause
            </Button>}
          <Button tone="quietDanger" onClick={() => onChoose({
        kind: "cancel"
      })}>
            Cancel subscription
          </Button>
        </div>}
    </>;
}
function Ready({
  userId,
  billing,
  onChange
}: {
  readonly userId: string;
  readonly billing: AdminBilling;
  readonly onChange: (next: AdminBilling) => void;
}) {
  const [action, setAction] = useState<Action | null>(null);
  function close(next: AdminBilling) {
    setAction(null);
    onChange(next);
  }
  return <>
      <Section title="Subscription">
        <SubscriptionActions userId={userId} billing={billing} action={action} onChoose={setAction} onClear={() => setAction(null)} onDone={close} />
      </Section>
      <Section title="Charges" count={billing.charges.length}>
        <Table label="Charges" rows={billing.charges} rowKey={row => row.id} empty="No charges yet." selected={row => action?.kind === "refund" && action.charge.id === row.id} columns={[{
        label: "Order",
        cell: row => <Truncate value={row.id} />
      }, {
        label: "Amount",
        numeric: true,
        cell: row => formatUsd(row.amountCents)
      }, {
        label: "Status",
        nowrap: true,
        cell: row => <Status tone={statusTone(row.status)}>{ORDER_STATUS[row.status]}</Status>
      }, {
        label: "When",
        numeric: true,
        phoneLabel: true,
        cell: row => day(row.createdAt)
      }, {
        label: "Actions",
        numeric: true,
        wide: true,
        cell: row => row.refundable ? <Button tone="quietDanger" onClick={() => setAction({
          kind: "refund",
          charge: row
        })}>
                    Refund
                  </Button> : null
      }]} />
        {action?.kind === "refund" ? <ActionForm key={action.charge.id} userId={userId} action={action} onCancel={() => setAction(null)} onDone={next => {
        setAction(null);
        onChange(next);
      }} /> : null}
      </Section>
      <Section title="Billing actions" count={billing.history.length}>
        <History rows={billing.history} empty="No pause, cancel or refund on this account yet." />
      </Section>
    </>;
}
export function Billing({
  userId
}: {
  readonly userId: string;
}) {
  const [loaded, replace] = useLoad(`billing-${userId}`, () => api<AdminBilling>(ROUTES.adminBilling, {
    params: {
      userId
    }
  }));
  if (loaded.state === "loading") return <Section title="Subscription">Loading billing</Section>;
  if (loaded.state === "failed") return <Section title="Subscription">
        <ErrorText>{refusal(loaded.error)}</ErrorText>
      </Section>;
  return <Ready userId={userId} billing={loaded.data} onChange={replace} />;
}